import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface SMSOptInSectionProps {
  customerPhone: string;
  onPhoneChange: (phone: string) => void;
  smsOptIn: boolean;
  onSmsOptInChange: (optIn: boolean) => void;
  smsUpdatesFee: number;
  onSmsUpdatesFeeChange: (fee: number) => void;
  estimatedSMSCount?: number;
}

const SMS_TOTAL_FEE = 1.00; // $1 total for SMS updates

export const SMSOptInSection: React.FC<SMSOptInSectionProps> = ({
  customerPhone,
  onPhoneChange,
  smsOptIn,
  onSmsOptInChange,
  smsUpdatesFee,
  onSmsUpdatesFeeChange,
}) => {
  const [phoneError, setPhoneError] = useState<string>('');
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [hasModifiedPhone, setHasModifiedPhone] = useState(false);

  // Auto-fill phone with verification requirement
  useEffect(() => {
    if (customerPhone && !hasModifiedPhone) {
      // Auto-fill but require modification
      setPhoneError('Please verify your number by editing it');
      setIsPhoneValid(false);
    }
  }, [customerPhone, hasModifiedPhone]);

  useEffect(() => {
    onSmsUpdatesFeeChange(smsOptIn ? SMS_TOTAL_FEE : 0);
  }, [smsOptIn, onSmsUpdatesFeeChange]);

  // Australian phone number validation
  const validateAustralianPhone = (phone: string): string => {
    if (!phone) return 'Phone number required';
    
    const cleaned = phone.replace(/\D/g, '');
    
    const mobilePatterns = [
      /^04\d{8}$/, // 04XXXXXXXX (10 digits)
      /^614\d{8}$/, // 614XXXXXXXX (11 digits)
    ];
    
    const isValid = mobilePatterns.some(pattern => pattern.test(cleaned));
    
    if (!isValid) {
      return 'Please enter a valid Australian mobile number';
    }
    
    return '';
  };

  const handlePhoneChange = (value: string) => {
    onPhoneChange(value);
    setHasModifiedPhone(true);
    const error = validateAustralianPhone(value);
    setPhoneError(error);
    setIsPhoneValid(!error && value.length > 0);
  };

  const handleOptInChange = (checked: boolean) => {
    if (checked && !isPhoneValid) {
      return; // Don't allow opt-in without valid phone
    }
    onSmsOptInChange(checked);
  };

  return (
    <Card className="border-2 border-dashed border-primary/20">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-5 w-5 text-primary" />
          SMS Order Updates
          <Badge variant="secondary" className="ml-auto">Optional</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Phone Number Input */}
        <div className="space-y-2">
          <Label htmlFor="customer-phone" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Mobile Number
          </Label>
          <Input
            id="customer-phone"
            type="tel"
            placeholder="04XX XXX XXX"
            value={customerPhone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            className={`${phoneError ? 'border-red-500' : isPhoneValid ? 'border-green-500' : ''}`}
          />
          {phoneError && (
            <p className="text-sm text-red-600">{phoneError}</p>
          )}
          {isPhoneValid && (
            <p className="text-sm text-green-600">✓ Valid number</p>
          )}
        </div>

        {/* SMS Opt-in Checkbox */}
        <div className="flex items-start space-x-3 p-4 bg-white rounded-lg border">
          <Checkbox
            id="sms-opt-in"
            checked={smsOptIn}
            onCheckedChange={handleOptInChange}
            disabled={!isPhoneValid}
            className="mt-1"
          />
          <div className="flex-1">
            <Label 
              htmlFor="sms-opt-in" 
              className="text-sm font-medium cursor-pointer"
            >
              Yes, send me SMS updates about my order
            </Label>
            <div className="text-sm text-muted-foreground mt-1">
              <strong className="text-primary">${SMS_TOTAL_FEE.toFixed(2)} total</strong> - Get notified when your flowers are ready
            </div>
          </div>
        </div>

        {/* Collapsible Details */}
        <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between text-sm text-muted-foreground">
              Why choose SMS updates?
              {isDetailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-3">
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Get instant notifications on your phone</li>
                <li>• No need to check email or log in</li>
                <li>• Know exactly when your flowers are ready</li>
                <li>• Perfect for time-sensitive deliveries</li>
              </ul>
            </div>
            
            <div className="text-xs text-muted-foreground p-3 bg-gray-50 rounded border">
              <strong>SMS Terms:</strong> By opting in, you consent to receive SMS notifications about your order. 
              Message frequency varies (typically 3 messages per order). Message and data rates may apply. 
              Reply STOP to opt out anytime. You must be 15+ years old.
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Fee confirmation when opted in */}
        {smsOptIn && (
          <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center justify-between text-sm">
              <span>SMS Updates Service</span>
              <span className="font-medium text-primary">+${SMS_TOTAL_FEE.toFixed(2)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SMSOptInSection; 